import { LocalStorageService } from '@mpt-fo/common/core/services/local-storage.service';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MobileWebViewService } from '@mpt-fo/common/core/services/mobile-web-view.service';
import { MoniepointAppParameters } from '@mpt-fo/common/core/models/login.model';
import { ScreenManagerService } from '@mpt-fo/common/core/services/screen-manager.service';
import { LoginHelperService } from '@mpt-fo/common/core/services/login-helper.service';
import { ApiClientHelper } from '@mpt-fo/common/core/helpers/api-client.helper';
import { OnboardingV2Service } from '@mpt-fo/common/core/services/onboarding-v2.service';
import { BtnType, IconType, ModalInfo } from '@mpt-fo/common/kamona/model/kamonal-model';
import { DeviceHelperService } from '@mpt-fo/common/core/services/device-helper.service';

@Component({
	selector: 'ng-mf-login',
	templateUrl: './login.component.html',
	styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit, OnDestroy {
	MOBILE_ROUTE = '/login/mobile';
	WEB_ROUTE = '/login/web';
  isUserFromSlashScreen: boolean;
  moniepointAppParameters: MoniepointAppParameters;
  screenManagerObserver: any;
  expiredSessionModal: boolean;
  modalInfo = ModalInfo;
  readonly expiredSessionTitle = 'Session Expired';
  readonly expiredSessionMessage =
    'You have been on this process for too long. For security reasons, we need to log you out. Log back in to continue.';
  isMobile: boolean;

	constructor(
		public router: Router,
		public localStorage: LocalStorageService,
		public mobileService: MobileWebViewService,
		private screenManager: ScreenManagerService,
		private route: ActivatedRoute,
		private loginHelperService: LoginHelperService,
		private apiClientHelper: ApiClientHelper,
		private onboardingService: OnboardingV2Service,
    private deviceHelper: DeviceHelperService
	) {
	}

	ngOnInit(): void {
    this.isMobile = this.screenManager.isMobile();
    this.screenManagerObserver = this.screenManager.isMobile$.subscribe((isMobile) => {
      this.isMobile = isMobile;
    });
    this.initialize();
	}

  ngOnDestroy() {
    this.screenManagerObserver?.unsubscribe();
    this.expiredSessionModal = false;
  }

  initialize(){
    const useNewSignIn = this.onboardingService.Config?.USE_KAMONA_SIGN_IN;
    (useNewSignIn) ? this.handleNewSignIn() : this.handleUsersFromSlashScreen();
  }

  clearSignupData(){
    if (this.onboardingService.Config?.USE_NEW_SIGN_UP_FLOW) {
      this.onboardingService.clearSignupData();
    }
  }

  handleUsersFromSlashScreen(){
    this.route.queryParams.subscribe((params) => {
      this.isUserFromSlashScreen = params?.isUserFromSlashScreen;
      (this.isTokenExpired()) ? this.handleExpiredToken() : this.handleDefaultSignIn();
    });
  }

  isTokenExpired(){
    return this.apiClientHelper.isTokenExpired();
  }

  handleExpiredToken(){
    this.modalInfo.btnText = 'Continue';
    this.modalInfo.btnType = BtnType.PRIMARY;
    this.modalInfo.iconType = IconType.INFO;
    this.modalInfo.title = this.expiredSessionTitle;
    this.modalInfo.description = this.expiredSessionMessage;
    this.expiredSessionModal = true;
  }

  handleDefaultSignIn(){
    this.clearSignupData();
    (this.isAlreadyLoggedIn()) ? this.handleAlreadyLoggedInUser() : this.handleNonLoggedInUser();
  }

  isAlreadyLoggedIn(){
    return this.localStorage.hasAccessToken();
  }

  handleNewSignIn(){
    this.onboardingService.routeToPage('/sign-in');
  }

  handleAlreadyLoggedInUser(){
    if(!this.mobileService.isMobileWebViewUser())
    this.keepUserLoggedIn();
    else
      this.handleNonLoggedInUser();
  }

  handleNonLoggedInUser(){
    this.clearStorage();
    this.deviceHelper.initialize();
    (this.mobileService.isMobileWebViewUser()) ? this.handleWebViewUserSignIn() : this.handleWebUserSignIn();
  }

  handleWebViewUserSignIn(){
    const loginParameters: MoniepointAppParameters = this.mobileService.getMoniepointAppParameters();
    this.setMoniepointAppParameters(loginParameters);
    (this.canRouteToSlashScreen()) ? this.routeToSlashScreen() : this.routeToMobileWeb();
  }

  handleWebUserSignIn(){
    this.handleWebRouting(this.isMobile);
  }

  handleWebRouting(isMobile: boolean){
    (isMobile) ? this.routeToMobileWeb() : this.routeToWeb();
  }

	keepUserLoggedIn() {
		this.loginHelperService.navigateUserAccordingly();
	}

  getMoniepointAppParameters(){
    return this.moniepointAppParameters;
  }

  setMoniepointAppParameters(moniepointAppParameters: MoniepointAppParameters){
    this.moniepointAppParameters = moniepointAppParameters;
  }

  canRouteToSlashScreen(){
    return !this.getReturningUser() && !this.isUserFromSlashScreen;
  }

  getReturningUser(){
    const loginParameters = this.getMoniepointAppParameters();
    return loginParameters?.deviceRegistrationRequest?.username;
  }

	routeToWeb() {
		this.router.navigateByUrl(this.WEB_ROUTE);
	}

  routeToMobileWeb() {
    this.router.navigateByUrl(this.MOBILE_ROUTE);
  }

  routeToSlashScreen() {
    this.router.navigateByUrl('/login/mobile/splash-screen');
  }

	clearStorage() {
		this.localStorage.deleteAll();
		sessionStorage.clear();

	}

  handleCloseModal(){
    this.clearSignupData();
    this.handleNonLoggedInUser();
  }
}
